import { useState, useCallback, useEffect } from 'react';
import { Loader2, Search, Check, RefreshCw, AlertCircle, Car, Hash, ChevronRight } from 'lucide-react';

// API pública da FIPE v1 (filtros) e v2 (busca por código)
const FIPE_API_V1 = 'https://parallelum.com.br/fipe/api/v1';
const FIPE_API_V2 = 'https://fipe.parallelum.com.br/api/v2';

interface FipeMarca {
  codigo: string;
  nome: string;
}

interface FipeModelo {
  codigo: number;
  nome: string;
}

interface FipeAno {
  codigo: string;
  nome: string;
}

export interface FipeResultado {
  valor: string;
  marca: string;
  modelo: string;
  anoModelo: number;
  combustivel: string;
  codigoFipe: string;
  mesReferencia: string;
  tipoVeiculo: number;
  siglaCombustivel: string;
}

// Anos retornados pela API v2 (busca por código)
interface FipeAnoV2 {
  code: string;
  name: string;
}

// Resultado da API v2 (busca por código)
interface FipeResultadoV2 {
  brand: string;
  model: string;
  modelYear: number;
  fuel: string;
  fipeCode: string;
  referenceMonth: string;
  vehicleType: number;
  fuelAcronym: string;
  price: string;
}

interface FipeBuscaProps {
  marcaNome?: string;
  modeloNome?: string;
  anoModelo?: number;
  codigoFipeInicial?: string;
  onResultado: (resultado: FipeResultado) => void;
  onCancel: () => void;
  modoInicial?: 'codigo' | 'filtros';
}

export function FipeBusca({
  marcaNome = '',
  modeloNome = '',
  anoModelo,
  codigoFipeInicial = '',
  onResultado,
  onCancel,
  modoInicial = 'filtros',
}: FipeBuscaProps) {
  // Modo de busca
  const [modoBusca, setModoBusca] = useState<'codigo' | 'filtros'>(modoInicial);

  // Busca por código FIPE (v2 API)
  const [codigoFipe, setCodigoFipe] = useState(codigoFipeInicial);
  const [anosDisponiveis, setAnosDisponiveis] = useState<FipeAnoV2[]>([]);
  const [, setSelectedAnoV2] = useState<FipeAnoV2 | null>(null);
  const [codigoStep, setCodigoStep] = useState<'codigo' | 'ano' | 'resultado'>('codigo');
  const [codigoLoading, setCodigoLoading] = useState(false);
  const [codigoError, setCodigoError] = useState<string | null>(null);

  // Busca por filtros
  const [buscaLoading, setBuscaLoading] = useState(false);
  const [buscaError, setBuscaError] = useState<string | null>(null);
  const [step, setStep] = useState<'marca' | 'modelo' | 'ano' | 'resultado'>('marca');

  // FIPE data
  const [marcas, setMarcas] = useState<FipeMarca[]>([]);
  const [modelos, setModelos] = useState<FipeModelo[]>([]);
  const [anos, setAnos] = useState<FipeAno[]>([]);
  const [resultado, setResultado] = useState<FipeResultado | null>(null);

  // Seleções
  const [selectedMarca, setSelectedMarca] = useState<FipeMarca | null>(null);
  const [selectedModelo, setSelectedModelo] = useState<FipeModelo | null>(null);
  const [selectedAno, setSelectedAno] = useState<FipeAno | null>(null);

  // Filtros de busca
  const [searchMarca, setSearchMarca] = useState('');
  const [searchModelo, setSearchModelo] = useState('');

  // ==================== BUSCA POR CÓDIGO (V2 API) ====================

  // Buscar anos disponíveis pelo código FIPE
  const fetchAnosByCodigo = async (codigo: string) => {
    setCodigoLoading(true);
    setCodigoError(null);
    setAnosDisponiveis([]);
    setSelectedAnoV2(null);

    try {
      // Formatar código (remover espaços, garantir formato correto)
      const codigoFormatado = codigo.trim().replace(/\s+/g, '');

      const response = await fetch(`${FIPE_API_V2}/cars/${codigoFormatado}/years`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Código FIPE não encontrado. Verifique se o código está correto.');
        }
        throw new Error('Erro ao buscar código FIPE');
      }

      const data: FipeAnoV2[] = await response.json();

      if (data.length === 0) {
        throw new Error('Nenhum ano encontrado para este código FIPE');
      }

      setAnosDisponiveis(data);

      // Se o veículo tem um ano definido, tentar encontrar automaticamente
      if (anoModelo) {
        const anoEncontrado = data.find(a => a.name.includes(anoModelo.toString()));
        if (anoEncontrado) {
          setSelectedAnoV2(anoEncontrado);
          fetchValorByCodigo(codigoFormatado, anoEncontrado.code);
          return;
        }
      }

      // Se só tem um ano, selecionar automaticamente
      if (data.length === 1) {
        setSelectedAnoV2(data[0]);
        fetchValorByCodigo(codigoFormatado, data[0].code);
        return;
      }

      setCodigoStep('ano');
    } catch (error) {
      setCodigoError(error instanceof Error ? error.message : 'Erro ao buscar código FIPE');
    } finally {
      setCodigoLoading(false);
    }
  };

  // Buscar valor pelo código FIPE e ano
  const fetchValorByCodigo = async (codigo: string, anoCodigo: string) => {
    setCodigoLoading(true);
    setCodigoError(null);

    try {
      const response = await fetch(`${FIPE_API_V2}/cars/${codigo}/years/${anoCodigo}`);

      if (!response.ok) {
        throw new Error('Erro ao buscar valor FIPE');
      }

      const data: FipeResultadoV2 = await response.json();

      // Converter para o formato esperado
      const resultadoConvertido: FipeResultado = {
        valor: data.price,
        marca: data.brand,
        modelo: data.model,
        anoModelo: data.modelYear,
        combustivel: data.fuel,
        codigoFipe: data.fipeCode,
        mesReferencia: data.referenceMonth,
        tipoVeiculo: data.vehicleType,
        siglaCombustivel: data.fuelAcronym || '',
      };

      setResultado(resultadoConvertido);
      setCodigoStep('resultado');
    } catch (error) {
      setCodigoError(error instanceof Error ? error.message : 'Erro ao buscar valor FIPE');
    } finally {
      setCodigoLoading(false);
    }
  };

  const handleBuscarPorCodigo = () => {
    if (!codigoFipe.trim()) return;
    fetchAnosByCodigo(codigoFipe);
  };

  const handleSelectAnoV2 = (ano: FipeAnoV2) => {
    setSelectedAnoV2(ano);
    fetchValorByCodigo(codigoFipe.trim(), ano.code);
  };

  const handleResetCodigo = () => {
    setCodigoFipe(codigoFipeInicial);
    setAnosDisponiveis([]);
    setSelectedAnoV2(null);
    setResultado(null);
    setCodigoStep('codigo');
    setCodigoError(null);
  };

  // ==================== BUSCA POR FILTROS (V1 API) ====================

  // Buscar marcas da FIPE
  const fetchMarcas = useCallback(async () => {
    setBuscaLoading(true);
    setBuscaError(null);
    try {
      const response = await fetch(`${FIPE_API_V1}/carros/marcas`);
      if (!response.ok) throw new Error('Erro ao buscar marcas');
      const data = await response.json();
      setMarcas(data);

      // Tentar encontrar a marca automaticamente
      if (marcaNome) {
        const found = data.find((m: FipeMarca) =>
          m.nome.toLowerCase().includes(marcaNome.toLowerCase()) ||
          marcaNome.toLowerCase().includes(m.nome.toLowerCase())
        );
        if (found) {
          setSelectedMarca(found);
          setSearchMarca(found.nome);
          setStep('modelo');
          fetchModelos(found.codigo);
          return;
        }
      }
      setStep('marca');
    } catch {
      setBuscaError('Erro ao buscar marcas da FIPE');
    } finally {
      setBuscaLoading(false);
    }
  }, [marcaNome]);

  // Buscar modelos da FIPE
  const fetchModelos = useCallback(async (marcaCodigo: string) => {
    setBuscaLoading(true);
    setBuscaError(null);
    try {
      const response = await fetch(`${FIPE_API_V1}/carros/marcas/${marcaCodigo}/modelos`);
      if (!response.ok) throw new Error('Erro ao buscar modelos');
      const data = await response.json();
      setModelos(data.modelos || []);

      // Tentar encontrar o modelo automaticamente
      if (modeloNome) {
        const found = data.modelos?.find((m: FipeModelo) =>
          m.nome.toLowerCase().includes(modeloNome.toLowerCase()) ||
          modeloNome.toLowerCase().includes(m.nome.toLowerCase())
        );
        if (found) {
          setSelectedModelo(found);
          setSearchModelo(found.nome);
          setStep('ano');
          fetchAnos(marcaCodigo, found.codigo.toString());
          return;
        }
      }
      setStep('modelo');
    } catch {
      setBuscaError('Erro ao buscar modelos da FIPE');
    } finally {
      setBuscaLoading(false);
    }
  }, [modeloNome]);

  // Buscar anos da FIPE
  const fetchAnos = useCallback(async (marcaCodigo: string, modeloCodigo: string) => {
    setBuscaLoading(true);
    setBuscaError(null);
    try {
      const response = await fetch(`${FIPE_API_V1}/carros/marcas/${marcaCodigo}/modelos/${modeloCodigo}/anos`);
      if (!response.ok) throw new Error('Erro ao buscar anos');
      const data = await response.json();
      setAnos(data);

      // Tentar encontrar o ano automaticamente
      if (anoModelo) {
        const found = data.find((a: FipeAno) => a.nome.includes(anoModelo.toString()));
        if (found) {
          setSelectedAno(found);
          fetchValor(marcaCodigo, modeloCodigo, found.codigo);
          return;
        }
      }
      setStep('ano');
    } catch {
      setBuscaError('Erro ao buscar anos da FIPE');
    } finally {
      setBuscaLoading(false);
    }
  }, [anoModelo]);

  // Buscar valor FIPE (por filtros)
  const fetchValor = async (marcaCodigo: string, modeloCodigo: string, anoCodigo: string) => {
    setBuscaLoading(true);
    setBuscaError(null);
    try {
      const response = await fetch(`${FIPE_API_V1}/carros/marcas/${marcaCodigo}/modelos/${modeloCodigo}/anos/${anoCodigo}`);
      if (!response.ok) throw new Error('Erro ao buscar valor');
      const data = await response.json();

      const resultadoConvertido: FipeResultado = {
        valor: data.Valor,
        marca: data.Marca,
        modelo: data.Modelo,
        anoModelo: data.AnoModelo,
        combustivel: data.Combustivel,
        codigoFipe: data.CodigoFipe,
        mesReferencia: data.MesReferencia,
        tipoVeiculo: data.TipoVeiculo,
        siglaCombustivel: data.SiglaCombustivel || '',
      };

      setResultado(resultadoConvertido);
      setStep('resultado');
    } catch {
      setBuscaError('Erro ao buscar valor da FIPE');
    } finally {
      setBuscaLoading(false);
    }
  };

  // Iniciar busca quando abrir modo filtros
  useEffect(() => {
    if (modoBusca === 'filtros' && marcas.length === 0) {
      fetchMarcas();
    }
  }, [modoBusca, fetchMarcas, marcas.length]);

  const handleSelectMarca = (marca: FipeMarca) => {
    setSelectedMarca(marca);
    setSelectedModelo(null);
    setSelectedAno(null);
    setResultado(null);
    setSearchModelo('');
    fetchModelos(marca.codigo);
  };

  const handleSelectModelo = (modelo: FipeModelo) => {
    if (!selectedMarca) return;
    setSelectedModelo(modelo);
    setSelectedAno(null);
    setResultado(null);
    fetchAnos(selectedMarca.codigo, modelo.codigo.toString());
  };

  const handleSelectAno = (ano: FipeAno) => {
    if (!selectedMarca || !selectedModelo) return;
    setSelectedAno(ano);
    setResultado(null);
    fetchValor(selectedMarca.codigo, selectedModelo.codigo.toString(), ano.codigo);
  };

  const handleReset = () => {
    setSelectedMarca(null);
    setSelectedModelo(null);
    setSelectedAno(null);
    setResultado(null);
    setStep('marca');
    setSearchMarca('');
    setSearchModelo('');
    if (modoBusca === 'filtros') {
      fetchMarcas();
    }
  };

  const handleConfirmarFiltros = () => {
    if (resultado) {
      onResultado(resultado);
    }
  };

  const filteredMarcas = marcas.filter((m) =>
    m.nome.toLowerCase().includes(searchMarca.toLowerCase())
  );

  const filteredModelos = modelos.filter((m) =>
    m.nome.toLowerCase().includes(searchModelo.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Tabs de modo */}
      <div className="flex border-b border-gray-200">
        <button
          type="button"
          onClick={() => {
            setModoBusca('codigo');
            setResultado(null);
            handleResetCodigo();
          }}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            modoBusca === 'codigo'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Hash className="w-4 h-4" />
          Buscar por Código
        </button>
        <button
          type="button"
          onClick={() => {
            setModoBusca('filtros');
            setResultado(null);
          }}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            modoBusca === 'filtros'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Search className="w-4 h-4" />
          Buscar por Filtros
        </button>
      </div>

      {/* Conteúdo baseado no modo */}
      {modoBusca === 'codigo' ? (
        <div className="space-y-4">
          {codigoStep === 'codigo' && (
            <>
              <p className="text-sm text-gray-600">
                Digite o código FIPE do veículo para obter o valor automaticamente.
              </p>

              <div className="flex gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    value={codigoFipe}
                    onChange={(e) => setCodigoFipe(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleBuscarPorCodigo()}
                    placeholder="Ex: 004278-1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleBuscarPorCodigo}
                  disabled={!codigoFipe.trim() || codigoLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
                >
                  {codigoLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  Buscar
                </button>
              </div>

              {codigoError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{codigoError}</span>
                </div>
              )}
            </>
          )}

          {codigoStep === 'ano' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Código <strong>{codigoFipe}</strong> - Selecione o ano/combustível:
                </p>
                <button
                  type="button"
                  onClick={handleResetCodigo}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Alterar código
                </button>
              </div>

              {codigoLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="max-h-48 overflow-auto space-y-1">
                  {anosDisponiveis.map((ano) => (
                    <button
                      key={ano.code}
                      type="button"
                      onClick={() => handleSelectAnoV2(ano)}
                      className={`
                        w-full px-3 py-2 text-left rounded-lg transition-colors text-sm
                        ${anoModelo && ano.name.includes(anoModelo.toString())
                          ? 'bg-blue-100 text-blue-700 border border-blue-300'
                          : 'bg-white hover:bg-gray-100 border border-gray-200'}
                      `}
                    >
                      {ano.name}
                    </button>
                  ))}
                </div>
              )}

              {codigoError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{codigoError}</span>
                </div>
              )}
            </div>
          )}

          {codigoStep === 'resultado' && resultado && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Código: <strong>{codigoFipe}</strong></span>
                <button
                  type="button"
                  onClick={handleResetCodigo}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Nova busca
                </button>
              </div>
              <ResultadoFipe resultado={resultado} />
            </div>
          )}

          {/* Footer do modo código */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            >
              Cancelar
            </button>
            {codigoStep === 'resultado' && resultado && (
              <button
                type="button"
                onClick={() => onResultado(resultado)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm"
              >
                <Check className="w-4 h-4" />
                Confirmar
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm bg-gray-50 p-2 rounded-lg">
            <Car className="w-4 h-4 text-blue-600" />
            <span className={selectedMarca ? 'text-blue-600 font-medium' : 'text-gray-500'}>
              {selectedMarca?.nome || 'Marca'}
            </span>
            {selectedModelo && (
              <>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <span className="text-blue-600 font-medium">{selectedModelo.nome}</span>
              </>
            )}
            {selectedAno && (
              <>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <span className="text-blue-600 font-medium">{selectedAno.nome}</span>
              </>
            )}
          </div>

          {/* Content */}
          <div className="min-h-[200px]">
            {buscaLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : buscaError ? (
              <div className="flex flex-col items-center justify-center py-8 text-red-600">
                <AlertCircle className="w-10 h-10 mb-2" />
                <p className="text-sm">{buscaError}</p>
                <button
                  type="button"
                  onClick={fetchMarcas}
                  className="mt-3 flex items-center gap-2 text-blue-600 hover:underline text-sm"
                >
                  <RefreshCw className="w-4 h-4" />
                  Tentar novamente
                </button>
              </div>
            ) : step === 'marca' ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">Selecione a marca:</p>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchMarca}
                    onChange={(e) => setSearchMarca(e.target.value)}
                    placeholder="Buscar marca..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>
                <div className="max-h-48 overflow-auto space-y-1">
                  {filteredMarcas.map((marca) => (
                    <button
                      key={marca.codigo}
                      type="button"
                      onClick={() => handleSelectMarca(marca)}
                      className={`
                        w-full px-3 py-2 text-left rounded-lg transition-colors text-sm
                        ${marcaNome && marca.nome.toLowerCase().includes(marcaNome.toLowerCase())
                          ? 'bg-blue-100 text-blue-700 border border-blue-300'
                          : 'bg-white hover:bg-gray-100 border border-gray-200'}
                      `}
                    >
                      {marca.nome}
                    </button>
                  ))}
                </div>
              </div>
            ) : step === 'modelo' ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">Selecione o modelo/versão:</p>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchModelo}
                    onChange={(e) => setSearchModelo(e.target.value)}
                    placeholder="Buscar modelo..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>
                <div className="max-h-48 overflow-auto space-y-1">
                  {filteredModelos.map((modelo) => (
                    <button
                      key={modelo.codigo}
                      type="button"
                      onClick={() => handleSelectModelo(modelo)}
                      className={`
                        w-full px-3 py-2 text-left rounded-lg transition-colors text-sm
                        ${modeloNome && modelo.nome.toLowerCase().includes(modeloNome.toLowerCase())
                          ? 'bg-blue-100 text-blue-700 border border-blue-300'
                          : 'bg-white hover:bg-gray-100 border border-gray-200'}
                      `}
                    >
                      {modelo.nome}
                    </button>
                  ))}
                </div>
              </div>
            ) : step === 'ano' ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">Selecione o ano/combustível:</p>
                <div className="max-h-48 overflow-auto space-y-1">
                  {anos.map((ano) => (
                    <button
                      key={ano.codigo}
                      type="button"
                      onClick={() => handleSelectAno(ano)}
                      className={`
                        w-full px-3 py-2 text-left rounded-lg transition-colors text-sm
                        ${anoModelo && ano.nome.includes(anoModelo.toString())
                          ? 'bg-blue-100 text-blue-700 border border-blue-300'
                          : 'bg-white hover:bg-gray-100 border border-gray-200'}
                      `}
                    >
                      {ano.nome}
                    </button>
                  ))}
                </div>
              </div>
            ) : resultado ? (
              <ResultadoFipe resultado={resultado} />
            ) : null}
          </div>

          {/* Footer da busca por filtros */}
          <div className="flex justify-between pt-4 border-t">
            <button
              type="button"
              onClick={handleReset}
              className="px-3 py-1.5 text-gray-600 hover:text-gray-800 text-sm"
            >
              Recomeçar
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                Cancelar
              </button>
              {resultado && (
                <button
                  type="button"
                  onClick={handleConfirmarFiltros}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm"
                >
                  <Check className="w-4 h-4" />
                  Confirmar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Componente auxiliar para exibir resultado
function ResultadoFipe({ resultado }: { resultado: FipeResultado }) {
  return (
    <div className="space-y-4">
      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center gap-2 text-green-700 mb-2">
          <Check className="w-5 h-5" />
          <span className="font-medium">Valor encontrado!</span>
        </div>
        <p className="text-3xl font-bold text-green-700">{resultado.valor}</p>
        <p className="text-sm text-green-600 mt-1">Ref: {resultado.mesReferencia}</p>
      </div>

      <div className="text-sm text-gray-600 grid grid-cols-2 gap-2">
        <p><strong>Código FIPE:</strong> {resultado.codigoFipe}</p>
        <p><strong>Combustível:</strong> {resultado.combustivel}</p>
        <p><strong>Marca:</strong> {resultado.marca}</p>
        <p><strong>Ano:</strong> {resultado.anoModelo}</p>
        <p className="col-span-2"><strong>Modelo:</strong> {resultado.modelo}</p>
      </div>
    </div>
  );
}
